import { useChannelSubscription } from '@/composables/useChannelSubscription'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import type {
  UnifiedChatActiveTarget,
  UnifiedChatCreateSessionOptions,
  UnifiedChatMode,
  UnifiedChatSessionReplaceBody,
  UnifiedChatSessionUpdateBody
} from '@/store/app/unifiedChat/types'
import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
import {
  AiChatSessionsService,
  AIChatSessionTypeEnum,
  CasesService,
  FlagEnum,
  type AIChatSession,
  type AIChatSessionRequest,
  type AvailableModel,
  type CustomerCase
} from '@/types/generated'
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { computed, readonly, ref } from 'vue'

/** Default title for newly created chat sessions (REST body). */
const DEFAULT_NEW_CHAT_TITLE = 'New Chat'

/** Persists floating side panel open state across full-page navigations (same tab). */
export const UNIFIED_CHAT_SIDE_PANEL_SESSION_STORAGE_KEY = 'jupus.unifiedChat.sidePanelOpen'
export const UNIFIED_CHAT_SIDE_PANEL_PREFER_EMPTY_SESSION_STORAGE_KEY =
  'jupus.unifiedChat.sidePanelPreferEmptySession'
export const UNIFIED_CHAT_TOP_NAV_OVERRIDE_EVENT = 'jupus:top-nav-override-change'
export type UnifiedChatTopNavOverride = 'none' | 'ai-assistant'

function withSessionStorage<T>(fn: () => T, fallback: T): T {
  if (typeof sessionStorage === 'undefined') {
    return fallback
  }
  try {
    return fn()
  } catch {
    return fallback
  }
}

function readSidePanelOpenFromSessionStorage(): boolean {
  return withSessionStorage(() => {
    const raw = sessionStorage.getItem(UNIFIED_CHAT_SIDE_PANEL_SESSION_STORAGE_KEY)
    if (!raw) {
      return false
    }
    return JSON.parse(raw) === true
  }, false)
}

function persistSidePanelOpenToSessionStorage(open: boolean): void {
  withSessionStorage(() => {
    if (open) {
      sessionStorage.setItem(UNIFIED_CHAT_SIDE_PANEL_SESSION_STORAGE_KEY, JSON.stringify(true))
    } else {
      sessionStorage.removeItem(UNIFIED_CHAT_SIDE_PANEL_SESSION_STORAGE_KEY)
    }
    return null
  }, null)
}

function readSidePanelPreferEmptyFromSessionStorage(): boolean {
  return withSessionStorage(() => {
    const raw = sessionStorage.getItem(UNIFIED_CHAT_SIDE_PANEL_PREFER_EMPTY_SESSION_STORAGE_KEY)
    if (!raw) {
      return false
    }
    return JSON.parse(raw) === true
  }, false)
}

function persistSidePanelPreferEmptyToSessionStorage(preferEmpty: boolean): void {
  withSessionStorage(() => {
    if (preferEmpty) {
      sessionStorage.setItem(
        UNIFIED_CHAT_SIDE_PANEL_PREFER_EMPTY_SESSION_STORAGE_KEY,
        JSON.stringify(true)
      )
    } else {
      sessionStorage.removeItem(UNIFIED_CHAT_SIDE_PANEL_PREFER_EMPTY_SESSION_STORAGE_KEY)
    }
    return null
  }, null)
}

/**
 * Last floating side-panel thread for reload / remount, scoped to route bucket (``global`` or
 * ``case:<uuid>``) so case vs non-case navigation still uses the correct list fallback.
 *
 * Includes ``date`` (YYYY-MM-DD) and ``lastLogin`` (ISO timestamp) so the snapshot can be
 * invalidated on a new calendar day or after a fresh login.
 */
export const UNIFIED_CHAT_SIDE_PANEL_SESSION_SNAPSHOT_KEY =
  'jupus.unifiedChat.sidePanelSessionSnapshot'

export interface UnifiedChatSidePanelSessionSnapshot {
  uuid: string
  routeKey: string
  /** Calendar date when the snapshot was written (YYYY-MM-DD). */
  date: string
  /** ``last_login`` value from the user endpoint at snapshot time. */
  lastLogin: string
}

/** Return today's date as YYYY-MM-DD in the user's local timezone. */
function todayDateString(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function readSidePanelSessionSnapshotFromStorage(): UnifiedChatSidePanelSessionSnapshot | null {
  return withSessionStorage(() => {
    const raw = sessionStorage.getItem(UNIFIED_CHAT_SIDE_PANEL_SESSION_SNAPSHOT_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as unknown
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as UnifiedChatSidePanelSessionSnapshot).uuid === 'string' &&
      typeof (parsed as UnifiedChatSidePanelSessionSnapshot).routeKey === 'string' &&
      typeof (parsed as UnifiedChatSidePanelSessionSnapshot).date === 'string' &&
      typeof (parsed as UnifiedChatSidePanelSessionSnapshot).lastLogin === 'string'
    ) {
      return parsed as UnifiedChatSidePanelSessionSnapshot
    }
    return null
  }, null)
}

function persistSidePanelSessionSnapshotToStorage(
  snapshot: UnifiedChatSidePanelSessionSnapshot | null
): void {
  withSessionStorage(() => {
    if (snapshot) {
      sessionStorage.setItem(UNIFIED_CHAT_SIDE_PANEL_SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot))
    } else {
      sessionStorage.removeItem(UNIFIED_CHAT_SIDE_PANEL_SESSION_SNAPSHOT_KEY)
    }
    return null
  }, null)
}

/** Thrown when `sessionType: CASE` is requested without any resolvable case UUID. */
export const CASE_SESSION_REQUIRES_CASE_UUID_ERROR =
  'CASE chat session requires options.caseUuid or a loaded current context case'

/**
 * Resolve the marker used to scope the side panel session snapshot to a login.
 *
 * Prefers ``last_login`` because it changes on every fresh login and therefore invalidates
 * the snapshot across logins. Falls back to ``uuid`` for the rare edge auth path where
 * ``last_login`` is not populated — in that case the snapshot is still sessionStorage-scoped
 * (dies on tab close) and the calendar-day staleness check independently invalidates it
 * after midnight. Returns ``null`` when no user is available at all so both callers short-
 * circuit the same way.
 *
 * This helper is shared between the side panel's auto-persist watcher and the fullscreen
 * collapse handler so both surfaces use the exact same scoping rule. Diverging behavior
 * between the two would cause the collapse path to silently fall back to ``aiChatSessionsList``
 * "latest for route bucket" — which can land on a different thread than the one the user
 * just collapsed.
 */
export function resolveSidePanelLoginMarker(
  user:
    | {
        last_login?: string | null
        uuid?: string | null
      }
    | null
    | undefined
): string | null {
  if (!user) return null
  return user.last_login ?? user.uuid ?? null
}

function aiChatSessionChannelName(sessionUuid: string | undefined): string | null {
  return sessionUuid ? `ai_chat_session_${sessionUuid}` : null
}

/**
 * Unified AI chat state (side panel + fullscreen slots, case context, REST + lazy WebSockets).
 *
 * **Errors (pick one layer per surface)**
 *
 * - Default for a single global indicator: computed `error` plus `clearError`. When both case and
 *   session errors are set, `error` is a short user-facing summary that does not repeat the scoped
 *   strings; use `caseContextError` and `sessionError` if the UI should show specific messages.
 * - Scoped only: `caseContextError` / `sessionError` next to case vs session UI. Do not duplicate
 *   the same failure in both a global `error` banner and scoped copy unless intentional.
 *
 * **Loading**
 *
 * - Default: computed `loading` (case or session work in progress).
 * - Scoped: `caseContextLoading` / `sessionLoading` when indicators are tied to those areas.
 *
 * **Async contracts**
 *
 * - `setContextCase` loads case context, sets `caseContextError` on failure, and does **not**
 *   rethrow. On failure it clears `currentContextCaseUuid` and `currentContextCase` so the UI
 *   shows no case badge for inaccessible routes. Concurrent calls are serialized by generation:
 *   only the latest request may
 *   update state. Every `setContextCaseUuidOnly` / `clearContextCase` bumps the same generation so
 *   in-flight case fetches cannot overwrite newer routing state (including `setContextCaseUuidOnly`
 *   when the UUID was already null).
 * - `createSession` / `loadSession` use a per-slot generation counter: only the latest operation
 *   for that target updates `active*Session`. Slower responses still resolve their promise with
 *   the fetched session, but the store keeps the newest winner.
 * - Other session helpers (`updateSession`, `replaceSession`, `deleteSession`) set `sessionError`
 *   on failure and **rethrow** the underlying error so callers can run additional handling.
 *
 * WebSockets: lazy — call `connectSessionWebSocket` when starting a stream (same idea as
 * AiChat.vue before send_message). If the active session UUID in a slot changes while
 * connected, call `disconnectSessionWebSocket` then `connectSessionWebSocket` again.
 */
export const useUnifiedChatStore = defineStore('unifiedChat', () => {
  const isSidePanelOpen = ref(readSidePanelOpenFromSessionStorage())
  const chatMode = ref<UnifiedChatMode>('sidepanel')
  const topNavOverride = ref<UnifiedChatTopNavOverride>('none')

  /** When true, route-based auto session load is skipped (user picked a thread from history). */
  const sidePanelRouteSessionPinned = ref(false)
  /** When true, keep an empty side panel until first send (“New chat”) despite route defaults. */
  const sidePanelPreferEmptySession = ref(readSidePanelPreferEmptyFromSessionStorage())

  /**
   * UUID of the session the side panel just "expanded" to the fullscreen KI-Assistent.
   * Consumed once by the AI_ASSISTANT route / view so the fresh-chat entry path is skipped
   * and the same session is preserved on the fullscreen page.
   */
  const pendingFullscreenSessionUuid = ref<string | null>(null)
  /**
   * Route path the user was on when expanding from the side panel. Used by the fullscreen
   * collapse control to send them back to the original page with the side panel reopened.
   */
  const fullscreenReturnRoute = ref<string | null>(null)
  /**
   * Route bucket key (``global`` or ``case:<uuid>``) of the return route. Stored alongside
   * the return path so the collapse handler can persist the correct session snapshot that
   * the reopened side panel picks up during its next ``syncSidePanelSessionToRoute`` run.
   */
  const fullscreenReturnRouteKey = ref<string | null>(null)
  /**
   * UUID of the session that originally triggered the expand flow. The fullscreen collapse
   * button is only shown while the active fullscreen session still matches this uuid, so a
   * user who starts a different chat after landing on ``/app/ai-assistant`` cannot collapse
   * that unrelated thread back into the side panel under the original page's route bucket.
   */
  const fullscreenSourceSessionUuid = ref<string | null>(null)
  /**
   * Set when the user expanded into fullscreen from the side panel's empty greeting state
   * (no session was active yet). Allows the fullscreen collapse control to render even
   * though :ref:`fullscreenSourceSessionUuid` is null — there is no source session to match
   * against because none existed at expand time.
   *
   * Cleared by :func:`consumeFullscreenReturnRoute`, :func:`clearFullscreenExpandState`,
   * and reset to ``false`` by :func:`expandSidePanelToFullscreen` so the with-session and
   * from-empty expand paths cannot bleed into each other.
   */
  const fullscreenExpandedFromEmpty = ref(false)

  const activeSidePanelSession = ref<AIChatSession | null>(null)
  const activeFullscreenSession = ref<AIChatSession | null>(null)

  const currentContextCaseUuid = ref<string | null>(null)
  const currentContextCase = ref<CustomerCase | null>(null)

  const caseContextLoading = ref(false)
  const sessionLoading = ref(false)
  /** Set when the case fetch fails; cleared on successful load or UUID-only updates. */
  const caseContextError = ref<string | null>(null)
  const sessionError = ref<string | null>(null)

  const availableModels = ref<AvailableModel[]>([])

  /** Bumps when a new `setContextCase` run should supersede in-flight fetches. */
  let contextCaseFetchGeneration = 0

  let sessionOpGenerationSidepanel = 0
  let sessionOpGenerationFullscreen = 0

  const bumpSessionOpGeneration = (target: UnifiedChatActiveTarget): number => {
    if (target === 'sidepanel') {
      return ++sessionOpGenerationSidepanel
    }
    return ++sessionOpGenerationFullscreen
  }

  const isLatestSessionOp = (target: UnifiedChatActiveTarget, gen: number): boolean => {
    return (
      gen ===
      (target === 'sidepanel' ? sessionOpGenerationSidepanel : sessionOpGenerationFullscreen)
    )
  }

  let sessionAsyncInFlight = 0

  const beginSessionAsync = () => {
    sessionAsyncInFlight += 1
    sessionLoading.value = true
  }

  const endSessionAsync = () => {
    sessionAsyncInFlight = Math.max(0, sessionAsyncInFlight - 1)
    sessionLoading.value = sessionAsyncInFlight > 0
  }

  const loading = computed(() => caseContextLoading.value || sessionLoading.value)

  /**
   * Single string suitable for one global banner or toast.
   *
   * When only one of case or session failed, this equals that scoped message. When both failed, it
   * is a generic combined line; read `caseContextError` and `sessionError` for the specific texts.
   * This is not a substitute for product copy if marketing strings are required — map from the
   * scoped refs instead.
   */
  const error = computed(() => {
    const caseErr = caseContextError.value
    const sessionErr = sessionError.value
    if (caseErr && sessionErr) {
      return 'Case context and chat session need attention. Check the details above or try again.'
    }
    return caseErr ?? sessionErr ?? null
  })

  /**
   * True when a context case UUID is selected and loaded (or set via `setContextCaseUuidOnly`).
   * After a failed `setContextCase` fetch, UUID is cleared and this is false while `caseContextError`
   * may still be set.
   */
  const hasContextCase = computed(() => !!currentContextCaseUuid.value)

  const sidePanelWebSocketChannel = ref('')
  const fullscreenWebSocketChannel = ref('')

  const sidePanelSessionWebSocket = useChannelSubscription(sidePanelWebSocketChannel, {
    disableAutoLifecycle: true,
    autoReconnect: false
  })

  const fullscreenSessionWebSocket = useChannelSubscription(fullscreenWebSocketChannel, {
    disableAutoLifecycle: true,
    autoReconnect: false
  })

  const getSessionWebSocket = (target: UnifiedChatActiveTarget) => {
    return target === 'sidepanel' ? sidePanelSessionWebSocket : fullscreenSessionWebSocket
  }

  const getWebSocketChannelRef = (target: UnifiedChatActiveTarget) => {
    return target === 'sidepanel' ? sidePanelWebSocketChannel : fullscreenWebSocketChannel
  }

  /**
   * Subscribe to the WebSocket for the **current** active session in the given slot.
   *
   * Lazily connects (same idea as AiChat.vue around send_message). Assigning a new session
   * via setActiveSession / loadSession does not switch the socket; after a UUID change, call
   * disconnectSessionWebSocket then this method again.
   */
  const connectSessionWebSocket = async (target: UnifiedChatActiveTarget) => {
    const session =
      target === 'sidepanel' ? activeSidePanelSession.value : activeFullscreenSession.value
    const channelRef = getWebSocketChannelRef(target)
    const ws = getSessionWebSocket(target)
    const next = aiChatSessionChannelName(session?.uuid)

    if (!next) {
      ws.unsubscribe()
      channelRef.value = ''
      return
    }

    channelRef.value = next
    await ws.subscribe()
  }

  /**
   * Drop the WebSocket for the given slot. Unsubscribes first, then clears the channel name.
   * Safe to call when already disconnected.
   */
  const disconnectSessionWebSocket = (target: UnifiedChatActiveTarget) => {
    getSessionWebSocket(target).unsubscribe()
    getWebSocketChannelRef(target).value = ''
  }

  const openSidePanel = () => {
    isSidePanelOpen.value = true
    persistSidePanelOpenToSessionStorage(true)
    if (chatMode.value !== 'fullscreen-overlay') {
      chatMode.value = 'sidepanel'
    }
  }

  const pinSidePanelRouteSession = () => {
    sidePanelRouteSessionPinned.value = true
  }

  const clearSidePanelRouteSessionPin = () => {
    sidePanelRouteSessionPinned.value = false
  }

  const setSidePanelPreferEmptySession = (value: boolean) => {
    sidePanelPreferEmptySession.value = value
    persistSidePanelPreferEmptyToSessionStorage(value)
  }

  /**
   * Read the persisted snapshot and validate it against the current date and login.
   *
   * Returns ``{ snapshot, stale: false }`` when valid, ``{ snapshot: null, stale: true }`` when
   * invalidated (new day / different login), or ``{ snapshot: null, stale: false }`` when nothing
   * was stored at all.
   */
  const readSidePanelSessionSnapshot = (
    currentLastLogin?: string | null
  ): { snapshot: UnifiedChatSidePanelSessionSnapshot | null; stale: boolean } => {
    const snapshot = readSidePanelSessionSnapshotFromStorage()
    if (!snapshot) return { snapshot: null, stale: false }

    // Defer restore until the caller has a known last_login (user store still hydrating).
    if (currentLastLogin == null) {
      return { snapshot: null, stale: false }
    }

    const isStale = snapshot.date !== todayDateString() || snapshot.lastLogin !== currentLastLogin

    if (isStale) {
      persistSidePanelSessionSnapshotToStorage(null)
      return { snapshot: null, stale: true }
    }

    return { snapshot, stale: false }
  }

  const persistSidePanelSessionSnapshot = (
    uuid: string,
    routeKey: string,
    lastLogin: string
  ): void => {
    persistSidePanelSessionSnapshotToStorage({ uuid, routeKey, date: todayDateString(), lastLogin })
  }

  const clearSidePanelSessionSnapshot = (): void => {
    persistSidePanelSessionSnapshotToStorage(null)
  }

  const closeSidePanel = () => {
    isSidePanelOpen.value = false
    persistSidePanelOpenToSessionStorage(false)
    sidePanelRouteSessionPinned.value = false
    sidePanelPreferEmptySession.value = false
    persistSidePanelPreferEmptyToSessionStorage(false)
    chatMode.value = 'sidepanel'
  }

  /**
   * Reset the given slot to the welcome/empty state for a new conversation.
   *
   * Disconnects the WebSocket, clears the active session, and (for the side panel)
   * sets the prefer-empty flag so route-based auto-load is skipped until the user
   * sends a message.
   *
   * The previous session is already persisted server-side — no explicit save is needed.
   */
  const startNewChat = (target: UnifiedChatActiveTarget = 'sidepanel') => {
    disconnectSessionWebSocket(target)
    setActiveSession(null, target)

    if (target === 'sidepanel') {
      sidePanelPreferEmptySession.value = true
      sidePanelRouteSessionPinned.value = false
      clearSidePanelSessionSnapshot()
    }
  }

  /**
   * Clear every piece of "side-panel expanded to fullscreen" bookkeeping so a subsequent
   * fresh entry into the fullscreen view does not surface a stale collapse button that
   * would send the user back to an unrelated page (or worse, persist a snapshot for a
   * different session under the original route bucket).
   *
   * Safe to call when nothing was staged — the refs are already ``null`` in that case.
   */
  const clearFullscreenExpandState = (): void => {
    pendingFullscreenSessionUuid.value = null
    fullscreenReturnRoute.value = null
    fullscreenReturnRouteKey.value = null
    fullscreenSourceSessionUuid.value = null
    fullscreenExpandedFromEmpty.value = false
  }

  /**
   * Enter fullscreen global chat with a fresh conversation when ``FLOATING_AI_BUTTON`` is on:
   * closes the floating panel, clears unified fullscreen slot state, and resets global chat UI
   * so no prior thread is resumed. When the flag is off, returns without changing state.
   *
   * Also clears any pending "expanded from side panel" bookkeeping so the collapse control
   * cannot survive into an unrelated fresh-chat visit — see :func:`clearFullscreenExpandState`.
   *
   * @returns Whether the fresh-entry path ran (``false`` when the feature flag is disabled).
   */
  const startNewFullscreenChat = async (): Promise<boolean> => {
    const floatingAiButton = useFeatureFlag(FlagEnum.FLOATING_AI_BUTTON)
    await floatingAiButton.checkFlag()
    if (!floatingAiButton.isEnabled.value) {
      return false
    }

    closeSidePanel()
    setChatMode('fullscreen')
    disconnectSessionWebSocket('fullscreen')
    setActiveSession(null, 'fullscreen')
    clearFullscreenExpandState()
    useGlobalChatStore().prepareFreshFullscreenGlobalEntry()
    return true
  }

  const toggleSidePanel = () => {
    if (chatMode.value === 'fullscreen-overlay') {
      closeSidePanel()
      return
    }
    if (isSidePanelOpen.value) {
      closeSidePanel()
    } else {
      openSidePanel()
    }
  }

  const clearError = () => {
    caseContextError.value = null
    sessionError.value = null
  }

  const clearCaseContextError = () => {
    caseContextError.value = null
  }

  const clearSessionError = () => {
    sessionError.value = null
  }

  const setChatMode = (mode: UnifiedChatMode) => {
    chatMode.value = mode
  }

  const setTopNavOverride = (override: UnifiedChatTopNavOverride) => {
    topNavOverride.value = override
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(UNIFIED_CHAT_TOP_NAV_OVERRIDE_EVENT, { detail: { override } })
      )
    }
  }

  /**
   * Transition from the floating side panel to the full `/app/ai-assistant` page while keeping
   * the same session open. Records the current route so the fullscreen collapse control can
   * return the user to it, and flags the target session uuid so the route's fresh-chat entry
   * path is skipped for this navigation only.
   *
   * Also tears down any pre-existing fullscreen slot (WebSocket + active session + chat mode)
   * that was left over from an earlier ``/app/ai-assistant`` visit. Without this, the route
   * guard's skip-``startNewFullscreenChat`` branch would leak the previous fullscreen channel:
   * :func:`setActiveSession` deliberately does not reconnect WebSockets, and the lazy connect
   * in ``useChatStream`` only fires on the first send — so the old channel would continue to
   * receive events for a thread the user is no longer viewing.
   *
   * :param sessionUuid: UUID of the session currently active in the side panel.
   * :param returnRoute: Route path the user is on at the time of expansion.
   * :param returnRouteKey: Bucket key (``global`` or ``case:<uuid>``) for the route, used
   *     by the collapse flow to persist the correct session snapshot for the reopened
   *     side panel.
   */
  const expandSidePanelToFullscreen = (
    sessionUuid: string,
    returnRoute: string,
    returnRouteKey: string
  ): void => {
    disconnectSessionWebSocket('fullscreen')
    setActiveSession(null, 'fullscreen')
    setChatMode('fullscreen')
    pendingFullscreenSessionUuid.value = sessionUuid
    fullscreenReturnRoute.value = returnRoute
    fullscreenReturnRouteKey.value = returnRouteKey
    fullscreenSourceSessionUuid.value = sessionUuid
    fullscreenExpandedFromEmpty.value = false
  }

  /**
   * Mark the fullscreen view as "expanded from an empty side panel" so the collapse
   * control becomes visible despite the absence of a source session.
   *
   * The from-empty path takes the standard ``/app/ai-assistant`` fresh-entry route through
   * the router guard's :func:`startNewFullscreenChat`, which calls
   * :func:`clearFullscreenExpandState` and would otherwise wipe any return-route data
   * staged ahead of time. Callers MUST therefore wait until the navigation has resolved
   * before invoking this — by then ``startNewFullscreenChat`` has already run and we can
   * record the bookkeeping cleanly without it being clobbered.
   *
   * Pairs with :func:`collapseFullscreenToSidePanel`, which clears the flag on consume.
   *
   * :param returnRoute: Route path the user was on at the time of expand.
   * :param returnRouteKey: Bucket key (``global`` or ``case:<uuid>``) for the return
   *     route, used by the collapse flow to scope any session snapshot it writes.
   * :return: None.
   */
  const markFullscreenExpandedFromEmpty = (returnRoute: string, returnRouteKey: string): void => {
    fullscreenReturnRoute.value = returnRoute
    fullscreenReturnRouteKey.value = returnRouteKey
    fullscreenSourceSessionUuid.value = null
    fullscreenExpandedFromEmpty.value = true
  }

  /**
   * Read and clear the pending fullscreen session uuid in one step. Called by the route guard
   * and the fullscreen view on mount so the flag only influences the very next navigation.
   *
   * :return: The previously-stored UUID, or ``null`` when no expansion is pending.
   */
  const consumePendingFullscreenSessionUuid = (): string | null => {
    const uuid = pendingFullscreenSessionUuid.value
    pendingFullscreenSessionUuid.value = null
    return uuid
  }

  /**
   * Read and clear the stored return route and its bucket key used by the fullscreen collapse
   * control. The fullscreen view reads this to know where to navigate back to when the user
   * collapses from the full page view to the floating side panel.
   *
   * Clears ``fullscreenSourceSessionUuid`` at the same time so the collapse button disappears
   * atomically on collapse — a second click cannot re-send the user through a half-consumed
   * flow.
   *
   * :return: Tuple of ``{ route, routeKey }`` — both ``null`` when nothing was set.
   */
  const consumeFullscreenReturnRoute = (): { route: string | null; routeKey: string | null } => {
    const route = fullscreenReturnRoute.value
    const routeKey = fullscreenReturnRouteKey.value
    fullscreenReturnRoute.value = null
    fullscreenReturnRouteKey.value = null
    fullscreenSourceSessionUuid.value = null
    fullscreenExpandedFromEmpty.value = false
    return { route, routeKey }
  }

  /**
   * Prepare the floating side panel to resume ``sessionUuid`` once the user navigates back to
   * ``returnRoute``. Persists a matching side panel snapshot (so the existing route-sync path
   * in :class:`SidePanelChat` picks it up on mount) and reopens the panel. Returns the stored
   * return path for the caller to use with the router.
   *
   * ``loginMarker`` must come from :func:`resolveSidePanelLoginMarker` so this writer stays in
   * lock-step with the side panel's auto-persist watcher. Using a different rule here would
   * cause mismatched snapshots: a user collapsing would write one scope while the side panel
   * on mount would read another and fall back to "latest session for this route", landing on
   * a different thread than the one they just collapsed.
   *
   * :param sessionUuid: UUID of the active fullscreen session that should follow the user
   *     back into the side panel. Pass ``null`` to skip the snapshot write — used by the
   *     "expanded from empty side panel" collapse path where the user has not yet sent a
   *     message in fullscreen, so there is no thread to resume; the reopened side panel
   *     falls back to its normal route-sync (welcome screen or latest-for-route).
   * :param loginMarker: Login-scoped marker from :func:`resolveSidePanelLoginMarker`. When
   *     ``null`` the snapshot write is skipped and the reopened side panel falls back to its
   *     normal route-sync path.
   * :return: The stored return path (``null`` when no expansion was recorded).
   */
  const collapseFullscreenToSidePanel = (
    sessionUuid: string | null,
    loginMarker: string | null | undefined
  ): string | null => {
    const { route, routeKey } = consumeFullscreenReturnRoute()
    if (sessionUuid && route && routeKey && loginMarker) {
      persistSidePanelSessionSnapshot(sessionUuid, routeKey, loginMarker)
    }
    openSidePanel()
    return route
  }

  const setContextCaseUuidOnly = (uuid: string | null) => {
    caseContextError.value = null
    // Invalidate any in-flight `setContextCase` (including clear while UUID already matches null).
    contextCaseFetchGeneration += 1
    if (uuid !== currentContextCaseUuid.value) {
      currentContextCase.value = null
    }
    currentContextCaseUuid.value = uuid
    if (!uuid) {
      currentContextCase.value = null
    }
  }

  const clearContextCase = () => {
    contextCaseFetchGeneration += 1
    caseContextLoading.value = false
    caseContextError.value = null
    currentContextCaseUuid.value = null
    currentContextCase.value = null
  }

  const setContextCase = async (uuid: string | null) => {
    if (!uuid) {
      caseContextError.value = null
      clearContextCase()
      return
    }

    const gen = (contextCaseFetchGeneration += 1)
    caseContextLoading.value = true
    caseContextError.value = null
    currentContextCaseUuid.value = null
    currentContextCase.value = null

    try {
      const caseRecord = await CasesService.casesRetrieve({ uuid })
      if (gen !== contextCaseFetchGeneration) {
        return
      }
      currentContextCaseUuid.value = uuid
      currentContextCase.value = caseRecord as CustomerCase
    } catch (err) {
      if (gen !== contextCaseFetchGeneration) {
        return
      }
      caseContextError.value = 'Failed to load case for chat context'
      currentContextCase.value = null
      currentContextCaseUuid.value = null
      if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
        console.debug('[unifiedChat] setContextCase failed', err)
      }
    } finally {
      if (gen === contextCaseFetchGeneration) {
        caseContextLoading.value = false
      }
    }
  }

  /**
   * Sets the active session for a UI slot only; does not reconnect WebSockets (see
   * connectSessionWebSocket).
   */
  const setActiveSession = (session: AIChatSession | null, target: UnifiedChatActiveTarget) => {
    if (target === 'sidepanel') {
      activeSidePanelSession.value = session
    } else {
      activeFullscreenSession.value = session
    }
  }

  const refreshActiveSessionIfMatching = (uuid: string, session: AIChatSession) => {
    if (activeSidePanelSession.value?.uuid === uuid) {
      activeSidePanelSession.value = session
    }
    if (activeFullscreenSession.value?.uuid === uuid) {
      activeFullscreenSession.value = session
    }
  }

  /**
   * Full-cleanup helper for a deleted session UUID. Tears down any active slot that
   * still references the session (disconnecting its WebSocket) and, crucially,
   * invalidates the persisted side-panel snapshot whenever it points at the same
   * UUID. Without the snapshot cleanup, a user who deletes a session in the
   * fullscreen surface and later opens the side panel from a different page would
   * trigger a 404 resume attempt on that stale UUID, which the global API
   * interceptor surfaces as a generic "unexpected error" toast.
   *
   * The storage read uses the raw (non-login-gated) path because we only want to
   * evict a known-stale UUID — staleness gating for resume is irrelevant for
   * cleanup.
   *
   * :param uuid: UUID of the session that was just deleted (any origin: unified
   *     store, global chat store, websocket-driven delete, etc.).
   */
  const clearActiveSessionsMatchingDeleted = (uuid: string) => {
    if (activeSidePanelSession.value?.uuid === uuid) {
      disconnectSessionWebSocket('sidepanel')
      activeSidePanelSession.value = null
    }
    if (activeFullscreenSession.value?.uuid === uuid) {
      disconnectSessionWebSocket('fullscreen')
      activeFullscreenSession.value = null
    }
    if (readSidePanelSessionSnapshotFromStorage()?.uuid === uuid) {
      clearSidePanelSessionSnapshot()
    }
  }

  const buildCreateRequestBody = (
    options?: UnifiedChatCreateSessionOptions
  ): AIChatSessionRequest => {
    const forcedType = options?.sessionType
    const contextCaseUuid =
      currentContextCaseUuid.value != null ? currentContextCaseUuid.value : undefined
    const explicitCaseUuid = options?.caseUuid

    if (forcedType === AIChatSessionTypeEnum.CASE) {
      const resolvedCaseUuid = explicitCaseUuid ?? contextCaseUuid
      if (resolvedCaseUuid === undefined) {
        throw new Error(CASE_SESSION_REQUIRES_CASE_UUID_ERROR)
      }
      return {
        session_type: AIChatSessionTypeEnum.CASE,
        case_uuid: resolvedCaseUuid,
        title: options?.title ?? DEFAULT_NEW_CHAT_TITLE,
        ...(options?.courtDbEnabled && { court_db_enabled: true })
      }
    }

    if (forcedType === AIChatSessionTypeEnum.GLOBAL) {
      const body: AIChatSessionRequest = {
        session_type: AIChatSessionTypeEnum.GLOBAL,
        title: options?.title ?? DEFAULT_NEW_CHAT_TITLE
      }
      if (options?.sessionGroupUuid) {
        body.session_group_uuid = options.sessionGroupUuid
      }
      if (options?.courtDbEnabled) {
        body.court_db_enabled = true
      }
      return body
    }

    const inferredCaseUuid = explicitCaseUuid ?? contextCaseUuid
    if (inferredCaseUuid) {
      return {
        session_type: AIChatSessionTypeEnum.UNIFIED,
        case_uuid: inferredCaseUuid,
        title: options?.title ?? DEFAULT_NEW_CHAT_TITLE,
        ...(options?.courtDbEnabled && { court_db_enabled: true })
      }
    }

    const body: AIChatSessionRequest = {
      session_type: AIChatSessionTypeEnum.UNIFIED,
      title: options?.title ?? DEFAULT_NEW_CHAT_TITLE
    }
    if (options?.sessionGroupUuid) {
      body.session_group_uuid = options.sessionGroupUuid
    }
    if (options?.courtDbEnabled) {
      body.court_db_enabled = true
    }
    return body
  }

  const createSession = async (
    options?: UnifiedChatCreateSessionOptions,
    target: UnifiedChatActiveTarget = 'sidepanel'
  ) => {
    const gen = bumpSessionOpGeneration(target)
    beginSessionAsync()
    sessionError.value = null

    try {
      const session = await AiChatSessionsService.aiChatSessionsCreate({
        requestBody: buildCreateRequestBody(options)
      })

      if (isLatestSessionOp(target, gen)) {
        setActiveSession(session, target)
      }
      return session
    } catch (err) {
      if (err instanceof Error && err.message === CASE_SESSION_REQUIRES_CASE_UUID_ERROR) {
        sessionError.value = err.message
      } else {
        sessionError.value = 'Failed to create chat session'
      }
      throw err
    } finally {
      endSessionAsync()
    }
  }

  const loadSession = async (uuid: string, target: UnifiedChatActiveTarget = 'sidepanel') => {
    const gen = bumpSessionOpGeneration(target)
    beginSessionAsync()
    sessionError.value = null

    try {
      const session = await AiChatSessionsService.aiChatSessionsRetrieve({ uuid })
      if (isLatestSessionOp(target, gen)) {
        setActiveSession(session, target)
      }
      return session
    } catch (err) {
      sessionError.value = 'Failed to load chat session'
      throw err
    } finally {
      endSessionAsync()
    }
  }

  /**
   * PATCH session fields. Prefer this for title, flags, and other partial updates.
   */
  const updateSession = async (uuid: string, requestBody: UnifiedChatSessionUpdateBody) => {
    beginSessionAsync()
    sessionError.value = null

    try {
      const session = await AiChatSessionsService.aiChatSessionsPartialUpdate({
        uuid,
        requestBody
      })

      refreshActiveSessionIfMatching(uuid, session)

      return session
    } catch (err) {
      sessionError.value = 'Failed to update chat session'
      throw err
    } finally {
      endSessionAsync()
    }
  }

  /**
   * PUT full session document. Use only when replacing the entire resource; routine edits
   * should use updateSession (PATCH).
   */
  const replaceSession = async (uuid: string, requestBody: UnifiedChatSessionReplaceBody) => {
    beginSessionAsync()
    sessionError.value = null

    try {
      const session = await AiChatSessionsService.aiChatSessionsUpdate({
        uuid,
        requestBody
      })

      refreshActiveSessionIfMatching(uuid, session)

      return session
    } catch (err) {
      sessionError.value = 'Failed to replace chat session'
      throw err
    } finally {
      endSessionAsync()
    }
  }

  const deleteSession = async (uuid: string) => {
    beginSessionAsync()
    sessionError.value = null

    try {
      await AiChatSessionsService.aiChatSessionsDestroy({ uuid })
      // clearActiveSessionsMatchingDeleted handles active-slot teardown AND the
      // persisted side-panel snapshot eviction, so the same cleanup is applied
      // whether deletion is initiated here or from the global chat store flow.
      clearActiveSessionsMatchingDeleted(uuid)
    } catch (err) {
      sessionError.value = 'Failed to delete chat session'
      throw err
    } finally {
      endSessionAsync()
    }
  }

  const fetchAvailableModels = async () => {
    try {
      const response = (await AiChatSessionsService.aiChatSessionsAvailableModelsList()) as any
      availableModels.value = response.models ?? response.results ?? []
    } catch (err) {
      Sentry.captureException(err, {
        tags: { endpoint: 'ai_chat_sessions_available_models' }
      })
      // On a transient failure, preserve any previously-fetched list instead
      // of wiping the dropdown — a blank selector is worse than a slightly
      // stale one. On the very first fetch the list is already [] so the
      // selector stays hidden naturally; no explicit reset needed here.
    }
  }

  return {
    isSidePanelOpen,
    sidePanelRouteSessionPinned,
    sidePanelPreferEmptySession,
    chatMode,
    pendingFullscreenSessionUuid: readonly(pendingFullscreenSessionUuid),
    fullscreenReturnRoute: readonly(fullscreenReturnRoute),
    fullscreenReturnRouteKey: readonly(fullscreenReturnRouteKey),
    fullscreenSourceSessionUuid: readonly(fullscreenSourceSessionUuid),
    fullscreenExpandedFromEmpty: readonly(fullscreenExpandedFromEmpty),
    activeSidePanelSession,
    activeFullscreenSession,
    currentContextCaseUuid,
    currentContextCase,
    caseContextLoading,
    sessionLoading,
    loading,
    caseContextError,
    sessionError,
    error,
    hasContextCase,
    // Readonly: channel is driven only by connect/disconnectSessionWebSocket.
    sidePanelWebSocketChannel: readonly(sidePanelWebSocketChannel),
    fullscreenWebSocketChannel: readonly(fullscreenWebSocketChannel),
    sidePanelSessionWebSocket,
    fullscreenSessionWebSocket,
    getSessionWebSocket,
    connectSessionWebSocket,
    disconnectSessionWebSocket,
    openSidePanel,
    closeSidePanel,
    startNewChat,
    startNewFullscreenChat,
    toggleSidePanel,
    readSidePanelSessionSnapshot,
    persistSidePanelSessionSnapshot,
    clearSidePanelSessionSnapshot,
    pinSidePanelRouteSession,
    clearSidePanelRouteSessionPin,
    setSidePanelPreferEmptySession,
    clearError,
    clearCaseContextError,
    clearSessionError,
    setChatMode,
    topNavOverride: readonly(topNavOverride),
    setTopNavOverride,
    expandSidePanelToFullscreen,
    markFullscreenExpandedFromEmpty,
    consumePendingFullscreenSessionUuid,
    consumeFullscreenReturnRoute,
    collapseFullscreenToSidePanel,
    clearFullscreenExpandState,
    setContextCase,
    setContextCaseUuidOnly,
    clearContextCase,
    setActiveSession,
    refreshActiveSessionIfMatching,
    clearActiveSessionsMatchingDeleted,
    createSession,
    loadSession,
    updateSession,
    replaceSession,
    deleteSession,
    availableModels,
    fetchAvailableModels
  }
})
